import tzlookup from 'tz-lookup';

/**
 * Get the timezone name for a given latitude and longitude
 * @param latitude - Latitude coordinate
 * @param longitude - Longitude coordinate
 * @returns IANA timezone name (e.g., "America/New_York", "Pacific/Honolulu")
 */
export const getTimezoneFromCoordinates = (latitude: number, longitude: number): string => {
  try {
    const timezone = tzlookup(latitude, longitude);
    return timezone || 'UTC'; // Return timezone or UTC as fallback
  } catch (error) {
    console.error('Error getting timezone from coordinates:', error);
    return 'UTC';
  }
};

/**
 * Convert a UTC date to local time in a specific timezone
 * @param date - Date to convert (can be Date object or ISO string)
 * @param timezone - IANA timezone name (e.g., "America/New_York")
 * @returns Date object adjusted to the target timezone
 */
export const convertToTimezone = (date: Date | string, timezone: string): Date => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  try {
    // Create a formatter for the target timezone
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });

    const parts = formatter.formatToParts(dateObj);
    const partMap: Record<string, string> = {};
    parts.forEach(part => {
      if (part.type !== 'literal') {
        partMap[part.type] = part.value;
      }
    });

    // Reconstruct the date in the target timezone
    return new Date(
      `${partMap.year}-${partMap.month}-${partMap.day}T${partMap.hour}:${partMap.minute}:${partMap.second}`
    );
  } catch (error) {
    console.error('Error converting to timezone:', error);
    return dateObj; // Return original date if conversion fails
  }
};

/**
 * Format a date/time for display in the dive location's timezone
 * @param date - Date to format
 * @param latitude - Dive location latitude
 * @param longitude - Dive location longitude
 * @param format - Format options (12h/24h for time, date format)
 * @returns Formatted date/time string with timezone indicator
 */
export const formatDateTimeInLocation = (
  date: Date | string,
  latitude: number | undefined,
  longitude: number | undefined,
  format: { time12h: boolean; dateFormat?: string } = { time12h: false }
): { formatted: string; timezone: string; tzAbbr: string } => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  // If no coordinates, use UTC
  if (latitude === undefined || longitude === undefined) {
    return {
      formatted: dateObj.toLocaleString('en-US'),
      timezone: 'UTC',
      tzAbbr: 'UTC'
    };
  }

  const timezone = getTimezoneFromCoordinates(latitude, longitude);

  // Format the date in the dive location's timezone
  const options: Intl.DateTimeFormatOptions = {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: format.time12h,
    timeZoneName: 'short'
  };

  const formatted = dateObj.toLocaleString('en-US', options);

  // Extract timezone abbreviation (e.g., "EST", "PST")
  const tzAbbr = formatted.split(' ').pop() || timezone;

  return {
    formatted,
    timezone,
    tzAbbr
  };
};
